/**
 * Conta da Equatorial: quanto a distribuidora cobra do cliente no mês.
 *
 * A fonte é o código de barras da própria conta (`linha_digitavel_equatorial`), e não a
 * soma das parcelas que a validação monta (consumo × tarifa + bandeira + restituição +
 * não isenta + iluminação + outros). Em tarifa social a soma não fecha: na fatura 14432
 * (UC 348039701206, OUT/2026) a conta era R$ 46,94, a soma deu R$ 59,49 e o boleto único
 * 6938 saiu cobrando a diferença do cliente. Medido em 20/10/2026: de AGO a OUT/2026, 85
 * faturas gravavam um valor diferente do código de barras (20 das 26 de baixa renda).
 *
 * O código de barras é o que o banco cobra quando a Vigor paga a conta, e confere com o
 * total impresso. Sem código legível (conta sem linha, dígito verificador errado, boleto
 * sem valor), vale o valor gravado na fatura, como antes.
 */
import { valorDaLinhaDigitavel } from '../../supabase/functions/_shared/fatura-parser/boleto-pix.ts';

/**
 * Diferença a partir da qual a soma das parcelas e o código de barras "não batem".
 * Centavos de arredondamento entre as parcelas são normais e não pedem conferência.
 */
export const TOLERANCIA_CONTA_EQUATORIAL = 0.05;

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** Valor em reais do código de barras da conta da Equatorial; `null` sem código que sirva. */
export const valorNoCodigoDaEquatorial = (linha?: string | null): number | null => {
  const digitos = String(linha ?? '').replace(/\D/g, '');
  return digitos ? valorDaLinhaDigitavel(digitos) : null;
};

export interface ContaEquatorial {
  valor: number;
  /** De onde veio o valor: o código de barras da conta, ou o gravado na fatura. */
  fonte: 'codigo_barras' | 'gravado';
}

/** O valor da conta da Equatorial que o boleto único cobra e que a Vigor paga. */
export const contaEquatorial = (fatura: {
  linha_digitavel_equatorial?: string | null;
  valor_fatura_distribuidora?: number | string | null;
}): ContaEquatorial => {
  const doCodigo = valorNoCodigoDaEquatorial(fatura.linha_digitavel_equatorial);
  if (doCodigo !== null) return { valor: doCodigo, fonte: 'codigo_barras' };
  return { valor: r2(Number(fatura.valor_fatura_distribuidora) || 0), fonte: 'gravado' };
};

export interface ConferenciaSoma {
  /** Valor do código de barras; `null` quando a conta não tem código que sirva. */
  codigo: number | null;
  /** Soma das parcelas, arredondada. */
  soma: number;
  /** soma − código (0 sem código). */
  diferenca: number;
  /** A soma passa da tolerância em relação ao código: a validação pede conferência. */
  diverge: boolean;
  /** O que a validação grava em `valor_fatura_distribuidora`. */
  totalParaGravar: number;
}

/**
 * Conferência da tela de validação: a soma das parcelas contra o código de barras.
 *
 * A soma deixa de ser o total gravado quando existe código, mas continua sendo um bom
 * alarme: se ela não bate, alguma parcela foi lida errado — e uma tarifa errada também
 * muda a parte da Vigor, não só a conta da Equatorial (na 14432 a tarifa veio 1,055007
 * em vez de 1,14618). Sem código não há o que conferir e vale a soma, como sempre foi.
 */
export const conferirSomaComCodigo = (soma: number, linha?: string | null): ConferenciaSoma => {
  const somaR = r2(Number(soma) || 0);
  const codigo = valorNoCodigoDaEquatorial(linha);
  if (codigo === null) {
    return { codigo: null, soma: somaR, diferenca: 0, diverge: false, totalParaGravar: somaR };
  }
  const diferenca = r2(somaR - codigo);
  return {
    codigo,
    soma: somaR,
    diferenca,
    diverge: Math.abs(diferenca) > TOLERANCIA_CONTA_EQUATORIAL,
    totalParaGravar: codigo,
  };
};
